import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { type HaulRoute } from 'api/haul-route.api';
import { formatRoadName, parseBlockTtlMinutes } from './roadsImporter.helpers';

interface RouteTtlInput {
  hours: string;
  minutes: string;
}

export interface BlockRoadFormState {
  reason: string;
  // One shared auto-unblock time applied to every road being blocked in
  // this action (the target plus whichever overlapping roads are checked)
  // — kept consistent rather than letting each road drift to its own TTL.
  ttl: RouteTtlInput;
}

export interface BlockRoadSubmission {
  routeId: string;
  ttlMinutes: number | undefined;
}

interface BlockRoadDialogProps {
  target: HaulRoute | null;
  // Other routes that physically overlay `target` (the reverse leg of a
  // two-way road, or any other variant between the same two locations) —
  // see findOverlappingRoutes. Empty for a plain one-way road.
  overlappingRoutes: HaulRoute[];
  value: BlockRoadFormState;
  onChange: (next: BlockRoadFormState) => void;
  onClose: () => void;
  onSave: (blocks: BlockRoadSubmission[]) => void;
  saving: boolean;
}

export function BlockRoadDialog({
  target,
  overlappingRoutes,
  value,
  onChange,
  onClose,
  onSave,
  saving,
}: BlockRoadDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!target) return;
    setSelectedIds([
      target.routeId,
      ...overlappingRoutes.map((r) => r.routeId),
    ]);
  }, [target, overlappingRoutes]);

  const toggleId = (routeId: string) => {
    setSelectedIds((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId]
    );
  };

  const hasOverlap = overlappingRoutes.length > 0 && target != null;
  const ttlInvalid =
    parseBlockTtlMinutes(value.ttl.hours, value.ttl.minutes) === null;

  const renderTtlFields = (disabled: boolean): JSX.Element => (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField
        size="small"
        type="number"
        label={t('roads_importer_field_block_ttl_hours')}
        value={value.ttl.hours}
        error={ttlInvalid}
        disabled={disabled}
        onChange={(e) => {
          onChange({ ...value, ttl: { ...value.ttl, hours: e.target.value } });
        }}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="number"
        label={t('roads_importer_field_block_ttl_minutes')}
        value={value.ttl.minutes}
        error={ttlInvalid}
        disabled={disabled}
        onChange={(e) => {
          onChange({
            ...value,
            ttl: { ...value.ttl, minutes: e.target.value },
          });
        }}
        sx={{ flex: 1 }}
      />
    </Box>
  );

  return (
    <Dialog
      open={target != null}
      onClose={() => {
        if (!saving) onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_block_dialog_title', {
          name: target
            ? formatRoadName(target.roadName ?? target.routeKey)
            : '',
        })}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        <TextField
          autoFocus
          multiline
          minRows={2}
          size="small"
          label={t('roads_importer_field_block_reason')}
          placeholder={t('roads_importer_field_block_reason_placeholder')}
          value={value.reason}
          onChange={(e) => {
            onChange({ ...value, reason: e.target.value });
          }}
          sx={{ mt: 1 }}
        />

        {hasOverlap ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('roads_importer_block_overlap_note', {
                count: overlappingRoutes.length,
              })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {[target, ...overlappingRoutes].map((route) => {
                if (!route) return null;
                const checked = selectedIds.includes(route.routeId);
                return (
                  <FormControlLabel
                    key={route.routeId}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => {
                          toggleId(route.routeId);
                        }}
                        disabled={saving}
                      />
                    }
                    label={formatRoadName(route.roadName ?? route.routeKey)}
                  />
                );
              })}
            </Box>
          </Box>
        ) : null}

        {target && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('roads_importer_field_block_ttl')}
            </Typography>
            {renderTtlFields(saving)}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('roads_importer_block_ttl_hint')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={saving}
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={
            saving ? <CircularProgress size={14} /> : <BlockOutlinedIcon />
          }
          disabled={ttlInvalid || saving || selectedIds.length === 0}
          onClick={() => {
            const ttlMinutes =
              parseBlockTtlMinutes(value.ttl.hours, value.ttl.minutes) ??
              undefined;
            onSave(selectedIds.map((routeId) => ({ routeId, ttlMinutes })));
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_block')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
